// src/services/api.js
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

class ApiClient {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Get auth token from localStorage
  getAuthToken() {
    return localStorage.getItem('classtraks_token');
  }

  // Set auth token in localStorage
  setAuthToken(token) {
    localStorage.setItem('classtraks_token', token);
  }

  // Remove auth token
  removeAuthToken() {
    localStorage.removeItem('classtraks_token');
  }

  // Get auth headers
  getAuthHeaders() {
    const token = this.getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  // Generic request method
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;

    const config = {
      headers: {
        ...this.getAuthHeaders(),
        ...options.headers,
      },
      ...options,
    };

    // Add body for POST/PUT requests
    if (options.body) {
      // Check if body is FormData (for file uploads)
      if (options.body instanceof FormData) {
        config.body = options.body;
        // Don't set Content-Type for FormData - browser will set it with boundary
      } else if (typeof options.body === 'object') {
        config.body = JSON.stringify(options.body);
        config.headers['Content-Type'] = 'application/json';
      } else {
        config.body = options.body;
      }
    } else {
      // Set default Content-Type for non-file requests
      if (!config.headers['Content-Type']) {
        config.headers['Content-Type'] = 'application/json';
      }
    }

    try {
      console.log(`API Request: ${options.method || 'GET'} ${url}`);
      const response = await fetch(url, config);

      console.log(`API Response Status: ${response.status} ${response.statusText}`);
      console.log(`API Response Headers:`, {
        contentType: response.headers.get('content-type'),
        contentLength: response.headers.get('content-length')
      });

      // Handle non-JSON responses
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.error(`Non-JSON response received. Content-Type: ${contentType}`);
        throw new Error(`Invalid response format: ${response.status}`);
      }

      const data = await response.json();
      console.log(`API Response Data:`, data);

      if (!response.ok) {
        // Handle auth errors
        if (response.status === 401) {
          this.removeAuthToken();
          window.location.href = '/login';
          throw new Error('Authentication expired. Please log in again.');
        }

        throw new Error(data.message || data.error || `HTTP error! status: ${response.status}`);
      }

      console.log(`API Request successful, returning data`);
      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // Convenience methods
  async get(endpoint, params = {}) {
    const query = new URLSearchParams(params).toString();
    const url = query ? `${endpoint}?${query}` : endpoint;
    return this.request(url, { method: 'GET' });
  }

  async post(endpoint, body = {}) {
    return this.request(endpoint, { method: 'POST', body });
  }

  async put(endpoint, body = {}) {
    return this.request(endpoint, { method: 'PUT', body });
  }

  async delete(endpoint, body = null) {
    const options = { method: 'DELETE' };
    if (body) {
      options.body = body;
    }
    return this.request(endpoint, options);
  }

  // Health check
  async healthCheck() {
    return this.request('/health', { method: 'GET' });
  }
}

// Create singleton instance
const apiClient = new ApiClient();

export default apiClient;